import _ from 'lodash';
import { Design, DesignChangeMap } from '@app/types';
import { ChangesContainer } from './ChangesContainer';

type DesignChangeMapKey = keyof DesignChangeMap;

type DesignChangeMapElement = DesignChangeMap[DesignChangeMapKey];

export type ChangeSetWrapperProps = {
    clKey: keyof DesignChangeMap;
    filteredMap: DesignChangeMapElement | Record<string, any>;
    serialized?: Design;
};

export function ChangeSetWrapper({
  clKey,
  filteredMap,
  serialized
}: ChangeSetWrapperProps) {
  const notMapped = ['globalErrorHandling', 'globalExceptionHandling', 'globalLoopHandling', 'intentHandling'];
  const simpleChanges = ['languages', 'applicationVersion'];
  const sortedChanges = returnSortedChanges(filteredMap, clKey) || Object.entries(filteredMap ?? {});

  /**
   * Settings specific rendering
   * @param {*} changeMap
   * @returns jsx
   */
  const getSettingsChangesContainer = (changeMap: any) => {
    const clonedSettings = _.cloneDeep(changeMap['settings']);
    const settingsPanels: any[] = [];

    if (clonedSettings['intentHandling']) {
      delete clonedSettings['intentHandling']['inARowWatched'];
      delete clonedSettings['intentHandling']['xAndOutWatched'];

      Object.keys(clonedSettings['intentHandling']).forEach((iKey) => {
        clonedSettings[iKey] = clonedSettings['intentHandling'][iKey];
      });
    }

    if (clonedSettings['globalLoopHandling']) {
      Object.keys(clonedSettings['globalLoopHandling']).forEach((iKey) => {
        clonedSettings[iKey] = clonedSettings['globalLoopHandling'][iKey];
      });

      delete clonedSettings['globalLoopHandling'];
    }

    Object.keys(clonedSettings).sort((a, b) => (a > b) ? 1 : -1).forEach((key) => {
      const cl = changeMap['settings'][key];
      settingsPanels.push(
        <ChangesContainer
          changeMap={cl}
          key={key}
          listType={key}
          serialized={serialized}
        />
      );
    });

    return <div>{settingsPanels}</div>;
  };

  if (clKey === 'settings') {
    return getSettingsChangesContainer(filteredMap);
  }

  if (simpleChanges.includes(clKey)) {
    return (
      <ChangesContainer
        changeMap={[clKey, sortedChanges]}
        key={clKey}
        listType={clKey}
        serialized={serialized}
      />
    );
  }

  if (notMapped.includes(clKey)) {
    return (
      <ChangesContainer
        changeMap={sortedChanges}
        key={clKey}
        listType={clKey}
        serialized={serialized}
      />
    );
  }

  if (!Array.isArray(sortedChanges)) {
    return null;
  }

  return (
    <>
      {sortedChanges.map((changeMap, i) => {
        return (
          <ChangesContainer
            changeMap={changeMap}
            key={i}
            listType={clKey}
            serialized={serialized}
          />
        );
      })}
    </>
  );
};

const returnSortedChanges = (changeMap: DesignChangeMapElement = {}, key: keyof DesignChangeMap) => {
  switch (key) {
    case 'ccxml':
    case 'modules':
      return Object.entries(changeMap);
    case 'application-modules':
    case 'application-objects':
      return Object.entries(changeMap).sort((a, b) => {
        return (((a[1] as any).node.name < (b[1] as any).node.name) ? 1 : -1);
      }).sort((a, b) => {
        return (((a[1] as any).node._class > (b[1] as any).node._class) ? 1 : -1);
      });
    case 'application-variables':
      return Object.entries(changeMap).sort((a, b) => ((a[1] as any).node.name > (b[1] as any).node.name ? 1 : -1));
    case 'compoundIntents':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.name < (b[1] as any).object.name ? 1 : -1
      );
    case 'csvData':
    case 'functions':
    case 'graphQLSchemas':
    case 'unitTests':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.name > (b[1] as any).object.name ? 1 : -1
      );
    case 'application-intents':
      return Object.entries(changeMap).sort((a, b) => ((a[1] as any).node.name > (b[1] as any).node.name ? 1 : -1));
    case 'fillerPrompts':
      return Object.entries(changeMap).sort((a, b) => (a[0] > b[0] ? 1 : -1));
    case 'globalIntents':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.moduleProperties.name > (b[1] as any).object.moduleProperties.name ? 1 : -1
      );
    case 'applicationVersion':
    case 'intentHandling':
    case 'languages':
    case 'settings':
      return changeMap;
  }
};
